"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type MutableRefObject,
} from "react";

/** Whole decor hides after this instant (IST). */
const OFFER_ENDS_AT = new Date("2026-11-08T23:59:59+05:30").getTime();
const TIMER_LABEL = "Diwali offer ends in";
const PILL_TEXT = "🪔 Happy Diwali · Festive lifetime prices";
const FIREWORKS_EVERY = 2600;

const CSS = `
.dwl-toran-host{position:sticky;top:0;z-index:20;flex-shrink:0;height:46px;margin:0;padding-bottom:6px;overflow:hidden;pointer-events:none;max-width:100%;background:transparent}
.dwl-toran{display:block;width:100%;max-width:100%;height:auto}
.dwl-bulb{animation:dwlBulb 1.6s ease-in-out infinite}
@keyframes dwlBulb{0%,100%{opacity:.45}50%{opacity:1}}
.dwl-leafsway{transform-box:fill-box;transform-origin:50% 0;animation:dwlLeaf 3.2s ease-in-out infinite}
@keyframes dwlLeaf{0%,100%{transform:rotate(-5deg)}50%{transform:rotate(5deg)}}
.dwl-kandil{position:absolute;top:-12px;width:64px;height:136px;pointer-events:none;z-index:2;transform-origin:50% 0;animation:dwlSwing 4.2s ease-in-out infinite}
.dwl-kandil.l{left:0}
.dwl-kandil.r{right:0;animation-delay:-2.1s}
.dwl-kandil svg{display:block;width:64px;height:136px}
.dwl-kandil .glow{animation:dwlKGlow 2.4s ease-in-out infinite}
@keyframes dwlSwing{0%,100%{transform:rotate(-3.5deg)}50%{transform:rotate(3.5deg)}}
@keyframes dwlKGlow{0%,100%{opacity:.55}50%{opacity:1}}
.dwl-pill{display:inline-block;margin:0 auto 16px;padding:7px 16px;border-radius:999px;white-space:nowrap;text-align:center;max-width:100%;box-sizing:border-box;
font:600 13px/1.2 inherit;letter-spacing:.02em;color:#9a3412;
background:linear-gradient(90deg,rgba(255,159,28,.16),rgba(255,201,77,.28),rgba(255,159,28,.16));
border:1px solid rgba(180,83,9,.35);box-shadow:0 0 24px rgba(255,159,28,.16);animation:dwlPill 3s ease-in-out infinite}
.dark .dwl-pill{color:#ffd57a;border-color:rgba(255,201,77,.45)}
@keyframes dwlPill{0%,100%{box-shadow:0 0 18px rgba(255,159,28,.12)}50%{box-shadow:0 0 30px rgba(255,159,28,.32)}}
.dwl-timer{display:flex;flex-direction:column;align-items:center;gap:10px;margin-top:26px;max-width:100%;font-family:inherit}
.dwl-timer-label{font-size:12px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#b45309}
.dark .dwl-timer-label{color:#ffc94d}
.dwl-timer-row{display:flex;align-items:center;gap:8px}
.dwl-tbox{min-width:68px;padding:9px 6px 7px;border-radius:12px;text-align:center;line-height:1;
background:linear-gradient(180deg,rgba(255,201,77,.16),rgba(255,159,28,.05));border:1px solid rgba(180,83,9,.28);box-shadow:0 0 18px rgba(255,159,28,.1)}
.dark .dwl-tbox{border-color:rgba(255,201,77,.38)}
.dwl-tbox b{display:block;font-size:26px;font-weight:800;color:var(--dash-text-strong);font-variant-numeric:tabular-nums}
.dwl-tbox small{display:block;margin-top:5px;font-size:10px;font-weight:600;letter-spacing:.12em;text-transform:uppercase;color:#9a6b22}
.dark .dwl-tbox small{color:#d8b46a}
.dwl-tsep{font-size:22px;font-weight:800;color:#d97706;opacity:.7;animation:dwlBlink 1s steps(2,end) infinite}
.dark .dwl-tsep{color:#ffc94d}
@keyframes dwlBlink{50%{opacity:.15}}
.dwl-timer.is-urgent .dwl-tbox{border-color:rgba(255,77,77,.6);box-shadow:0 0 22px rgba(255,77,77,.22)}
.dwl-timer.is-urgent .dwl-timer-label{color:#dc2626}
.dark .dwl-timer.is-urgent .dwl-timer-label{color:#ff8a8a}
.dwl-card{transition:box-shadow .3s ease,border-color .3s ease}
.dwl-card:hover{box-shadow:0 0 0 1px rgba(255,201,77,.45),0 0 28px rgba(255,159,28,.16)!important}
.dwl-card--hero{animation:dwlHero 3.2s ease-in-out infinite}
@keyframes dwlHero{0%,100%{box-shadow:0 0 0 1px rgba(255,201,77,.35),0 0 26px rgba(255,159,28,.12),0 0 60px rgba(6,199,85,.06)}
50%{box-shadow:0 0 0 1px rgba(255,201,77,.7),0 0 40px rgba(255,159,28,.26),0 0 80px rgba(6,199,85,.12)}}
.dwl-garland{position:absolute;left:14px;top:-10px;width:calc(100% - 28px);max-width:calc(100% - 28px);height:30px;pointer-events:none;z-index:1;overflow:hidden;display:block}
.dwl-cdiya{position:absolute;top:-10px;right:-8px;width:34px;height:30px;pointer-events:none;z-index:2}
.dwl-cdiya svg{display:block;width:100%;height:100%}
.dwl-card--hero .dwl-cdiya{top:auto;bottom:14px;right:auto;left:-17px;width:40px;height:34px}
.dwl-card--hero .dwl-cdiya.r{left:auto;right:-17px}
.dwl-flame{transform-box:fill-box;transform-origin:50% 100%;animation:dwlFlick .18s ease-in-out infinite alternate}
@keyframes dwlFlick{from{transform:scaleY(1) rotate(-3deg)}to{transform:scaleY(1.15) rotate(3deg)}}
.dwl-btn{position:relative;overflow:hidden}
.dwl-btn::after{content:"";position:absolute;top:0;bottom:0;left:-60%;width:40%;pointer-events:none;
background:linear-gradient(100deg,transparent,rgba(255,255,255,.45),transparent);transform:skewX(-18deg);animation:dwlShine 3.6s ease-in-out infinite}
@keyframes dwlShine{0%,55%{left:-60%}100%{left:130%}}
.dwl-fx{position:fixed;inset:0;z-index:30;pointer-events:none}
.dwl-fx canvas{position:absolute;inset:0;width:100%;height:100%}
@media (max-width:1279px){.dwl-kandil{display:none}}
@media (max-width:640px){
.dwl-toran-host{height:30px}
.dwl-pill{font-size:12px;padding:6px 12px;white-space:normal}
.dwl-garland{left:8px;top:-8px;width:calc(100% - 16px);max-width:calc(100% - 16px)}
.dwl-cdiya{top:8px;right:8px;width:28px;height:24px}
.dwl-card--hero .dwl-cdiya{left:8px;bottom:10px;width:32px;height:28px}
.dwl-card--hero .dwl-cdiya.r{right:8px}
.dwl-tbox{min-width:0;width:54px;padding:8px 2px 6px}
.dwl-tbox b{font-size:20px}
.dwl-tbox small{font-size:9px;letter-spacing:.06em}
.dwl-tsep{font-size:16px}
.dwl-timer{margin-top:18px;gap:8px}
.dwl-timer-row{gap:4px;justify-content:center;max-width:100%}
.dwl-timer-label{letter-spacing:.08em;text-align:center}
}
@media (max-width:380px){
.dwl-tbox{width:46px;padding:7px 1px 5px}
.dwl-tbox b{font-size:17px}
.dwl-tbox small{font-size:8px;letter-spacing:.04em}
.dwl-tsep{font-size:14px}
.dwl-timer-label{font-size:10px;letter-spacing:.06em}
}
@media (prefers-reduced-motion:reduce){
.dwl-bulb,.dwl-leafsway,.dwl-kandil,.dwl-kandil .glow,.dwl-pill,.dwl-card--hero,.dwl-flame,.dwl-btn::after,.dwl-tsep{animation:none!important}
}
`;

type Burst = (x: number, y: number, scale?: number) => void;

type DiwaliContextValue = {
  active: boolean;
  burst: Burst;
};

const DiwaliContext = createContext<DiwaliContextValue>({
  active: false,
  burst: () => {},
});

function offerIsLive() {
  return Date.now() <= OFFER_ENDS_AT;
}

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export function useDiwali() {
  return useContext(DiwaliContext);
}

function marigolds(
  x0: number,
  y0: number,
  cx: number,
  cy: number,
  x1: number,
  y1: number,
  step: number,
  r: number,
) {
  const len = Math.abs(x1 - x0) + Math.abs(cy - y0);
  const n = Math.max(4, Math.round(len / step));
  let out = "";
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const a = (1 - t) * (1 - t);
    const b = 2 * (1 - t) * t;
    const c = t * t;
    const x = a * x0 + b * cx + c * x1;
    const y = a * y0 + b * cy + c * y1;
    const col = i % 3 === 1 ? "#ffc21a" : i % 3 === 2 ? "#ff7a00" : "#ff9a1f";
    const cx1 = x.toFixed(1);
    const cy1 = y.toFixed(1);
    out += `<circle cx="${cx1}" cy="${cy1}" r="${r}" fill="${col}"/>`;
    out += `<circle cx="${cx1}" cy="${cy1}" r="${r}" fill="none" stroke="#b45309" stroke-opacity=".55" stroke-width="1" stroke-dasharray="1.6 1.8"/>`;
    out += `<circle cx="${(x - r * 0.2).toFixed(1)}" cy="${(y - r * 0.25).toFixed(1)}" r="${(r * 0.32).toFixed(1)}" fill="#fff3c4" fill-opacity=".45"/>`;
  }
  return out;
}

function leaf(x: number, y: number, rot: number) {
  return `<g transform="translate(${x} ${y}) rotate(${rot})"><g class="dwl-leafsway"><path d="M0 0 C 7 6, 7 16, 0 24 C -7 16, -7 6, 0 0Z" fill="#15803d"/><path d="M0 2 L0 22" stroke="#4ade80" stroke-opacity=".6" stroke-width="1"/></g></g>`;
}

function buildToranSvg(width: number) {
  const W = Math.max(280, width);
  const k = W < 640 ? 0.62 : 1;
  const n = Math.max(3, Math.round(W / (175 * k)));
  const sw = W / n;
  const H = Math.round(46 * k);
  let s = `<svg class="dwl-toran" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" style="height:${H}px">`;
  s += `<line x1="0" y1="3" x2="${W}" y2="3" stroke="#5b4a36" stroke-width="2"/>`;
  for (let i = 0; i < n; i++) {
    const x0 = i * sw;
    const x1 = x0 + sw;
    s += marigolds(x0 + 6 * k, 4, (x0 + x1) / 2, 40 * k, x1 - 6 * k, 4, 10 * k, 5.2 * k);
  }
  for (let j = 0; j <= n; j++) {
    const x = Math.min(W - 8, Math.max(8, j * sw));
    s += `<g transform="translate(${x} 0) scale(${k}) translate(${-x} 0)">${leaf(x - 5, 5, 14)}${leaf(x + 5, 5, -14)}</g>`;
    s += `<circle class="dwl-bulb" style="animation-delay:${(j % 4) * 0.4}s" cx="${x}" cy="${30 * k}" r="${3.4 * k}" fill="#fff3c4"/>`;
    s += `<circle class="dwl-bulb" style="animation-delay:${(j % 4) * 0.4}s" cx="${x}" cy="${30 * k}" r="${8 * k}" fill="#ffc94d" fill-opacity=".25"/>`;
  }
  return `${s}</svg>`;
}

function kandilSvg(prefix: string, main: string, edge: string) {
  const g = `${prefix}g`;
  const h = `${prefix}h`;
  let tails = "";
  for (let i = 0; i < 7; i++) {
    const x = 20 + i * 5.7;
    const col = i % 2 ? main : "#ffc94d";
    tails += `<path d="M${x} 112 q 2 14 0 26 q -2 12 0 22" stroke="${col}" stroke-width="2.4" fill="none" stroke-linecap="round"/>`;
  }
  return `<svg viewBox="0 0 80 170" width="74" height="158" aria-hidden="true"><defs><linearGradient id="${g}" x1="0" x2="1"><stop offset="0" stop-color="${edge}"/><stop offset=".5" stop-color="${main}"/><stop offset="1" stop-color="${edge}"/></linearGradient><radialGradient id="${h}"><stop offset="0" stop-color="#ffd166" stop-opacity=".75"/><stop offset="1" stop-color="#ff9f1c" stop-opacity="0"/></radialGradient><linearGradient id="${g}s" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#8a7357" stop-opacity="0"/><stop offset="1" stop-color="#c9a46b"/></linearGradient></defs><rect x="39.3" y="0" width="1.4" height="40" fill="url(#${g}s)"/><circle class="glow" cx="40" cy="80" r="40" fill="url(#${h})"/><rect x="31" y="38" width="18" height="7" rx="2" fill="#ffc94d"/><path d="M40 45 L68 66 L68 94 L40 115 L12 94 L12 66Z" fill="url(#${g})" stroke="#ffc94d" stroke-width="2"/><path d="M40 52 L60 70 L60 90 L40 108 L20 90 L20 70Z" fill="#fff3c4" fill-opacity=".22" stroke="#ffe29a" stroke-opacity=".7" stroke-width="1.2"/><line x1="12" y1="80" x2="68" y2="80" stroke="#ffc94d" stroke-width="1.5" stroke-dasharray="2 3"/><circle cx="40" cy="80" r="6" fill="#fff3c4"/><path d="M28 115 L52 115" stroke="#ffc94d" stroke-width="3" stroke-linecap="round"/>${tails}</svg>`;
}

function diyaSvg(id: string) {
  return `<svg viewBox="-40 -48 80 66" aria-hidden="true"><defs><radialGradient id="${id}" cx="50%" cy="50%" r="50%"><stop offset="0" stop-color="#ffd166" stop-opacity=".9"/><stop offset="1" stop-color="#ff7a18" stop-opacity="0"/></radialGradient></defs><circle cx="2" cy="-26" r="24" fill="url(#${id})"/><path d="M-34 0 Q0 30 34 0 Q24 -4 0 -4 Q-24 -4 -34 0Z" fill="#c2410c"/><path d="M-34 0 Q0 30 34 0" fill="none" stroke="#fdba74" stroke-width="2"/><path d="M-20 6 Q0 18 20 6" fill="none" stroke="#ffc94d" stroke-width="2" stroke-dasharray="3 4"/><line x1="0" y1="-4" x2="2" y2="-12" stroke="#fde68a" stroke-width="2"/><path class="dwl-flame" d="M2 -40 C 12 -26, 10 -14, 2 -12 C -6 -14, -8 -26, 2 -40Z" fill="#ffb703"/><path class="dwl-flame" d="M2 -30 C 7 -22, 6 -16, 2 -14 C -2 -16, -3 -22, 2 -30Z" fill="#fff3c4"/></svg>`;
}

function cardGarlandSvg() {
  const W = 600;
  const n = 4;
  const sw = W / n;
  let s = `<svg class="dwl-garland" height="30" viewBox="0 0 ${W} 30" preserveAspectRatio="none" aria-hidden="true">`;
  for (let i = 0; i < n; i++) {
    const x0 = i * sw;
    const x1 = x0 + sw;
    s += marigolds(x0 + 4, 12, (x0 + x1) / 2, 30, x1 - 4, 12, 9, 4.6);
  }
  for (let j = 0; j <= n; j++) {
    s += leaf(Math.min(W - 6, Math.max(6, j * sw)), 10, 0);
  }
  return `${s}</svg>`;
}

function safeId(raw: string) {
  const cleaned = raw.replace(/[^a-zA-Z0-9]/g, "");
  return cleaned || "dwl";
}

export function DiwaliProvider({
  children,
  fireworks = false,
}: {
  children: ReactNode;
  fireworks?: boolean;
}) {
  const [active] = useState(offerIsLive);
  const burstRef = useRef<Burst>(() => {});
  const burst = useCallback<Burst>((x, y, scale) => {
    burstRef.current(x, y, scale);
  }, []);
  const value = useMemo(() => ({ active, burst }), [active, burst]);

  return (
    <DiwaliContext.Provider value={value}>
      {active ? <style>{CSS}</style> : null}
      {active && fireworks ? <FireworksLayer burstRef={burstRef} /> : null}
      {children}
    </DiwaliContext.Provider>
  );
}

export function DiwaliToran() {
  const { active } = useDiwali();
  const hostRef = useRef<HTMLDivElement>(null);
  const widthRef = useRef(0);
  const [markup, setMarkup] = useState("");

  useEffect(() => {
    if (!active) return;
    const el = hostRef.current;
    if (!el) return;
    const draw = () => {
      const width = Math.round(el.clientWidth);
      if (width < 8 || width === widthRef.current) return;
      widthRef.current = width;
      setMarkup(buildToranSvg(width));
    };
    draw();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", draw);
      return () => window.removeEventListener("resize", draw);
    }
    const observer = new ResizeObserver(draw);
    observer.observe(el);
    return () => observer.disconnect();
  }, [active]);

  if (!active) return null;
  return (
    <div ref={hostRef} className="dwl-toran-host" aria-hidden="true">
      {markup ? <div dangerouslySetInnerHTML={{ __html: markup }} /> : null}
    </div>
  );
}

export function DiwaliPill() {
  const { active } = useDiwali();
  if (!active) return null;
  return <div className="dwl-pill">{PILL_TEXT}</div>;
}

export function DiwaliKandils() {
  const { active } = useDiwali();
  const leftId = safeId(useId());
  const rightId = safeId(useId());
  if (!active) return null;
  return (
    <>
      <div
        className="dwl-kandil l"
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: kandilSvg(leftId, "#ff4d8d", "#c2185b") }}
      />
      <div
        className="dwl-kandil r"
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: kandilSvg(`${rightId}b`, "#ff7a18", "#c2410c") }}
      />
    </>
  );
}

function pad(n: number) {
  return n < 10 ? `0${n}` : String(n);
}

export function DiwaliTimer() {
  const { active } = useDiwali();
  const [left, setLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!active) return;
    const tick = () => setLeft(OFFER_ENDS_AT - Date.now());
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [active]);

  if (!active || (left !== null && left <= 0)) return null;

  const ms = left ?? 0;
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const parts = [
    ["d", "Days", pad(Math.floor(totalSeconds / 86400))],
    ["h", "Hours", pad(Math.floor((totalSeconds % 86400) / 3600))],
    ["m", "Mins", pad(Math.floor((totalSeconds % 3600) / 60))],
    ["s", "Secs", pad(totalSeconds % 60)],
  ] as const;
  const urgent = left !== null && left < 86_400_000;

  return (
    <div
      className={`dwl-timer${urgent ? " is-urgent" : ""}`}
      role={left === null ? undefined : "timer"}
      aria-label={TIMER_LABEL}
      aria-hidden={left === null ? true : undefined}
      style={left === null ? { visibility: "hidden" } : undefined}
    >
      <span className="dwl-timer-label">⏳ {TIMER_LABEL}</span>
      <span className="dwl-timer-row">
        {parts.map(([key, label, value], index) => (
          <span key={key} style={{ display: "contents" }}>
            <span className="dwl-tbox">
              <b>{value}</b>
              <small>{label}</small>
            </span>
            {index < parts.length - 1 ? (
              <span className="dwl-tsep" aria-hidden="true">
                :
              </span>
            ) : null}
          </span>
        ))}
      </span>
    </div>
  );
}

export function DiwaliCardDecor({ hero = false }: { hero?: boolean }) {
  const { active } = useDiwali();
  const diyaA = safeId(useId());
  const diyaB = safeId(useId());
  if (!active) return null;
  return (
    <>
      {hero ? <div aria-hidden="true" dangerouslySetInnerHTML={{ __html: cardGarlandSvg() }} /> : null}
      <div className="dwl-cdiya" aria-hidden="true" dangerouslySetInnerHTML={{ __html: diyaSvg(diyaA) }} />
      {hero ? (
        <div className="dwl-cdiya r" aria-hidden="true" dangerouslySetInnerHTML={{ __html: diyaSvg(`${diyaB}b`) }} />
      ) : null}
    </>
  );
}

export function DiwaliDealLink({ href, className }: { href: string; className: string }) {
  const { active, burst } = useDiwali();

  const onPointerDown = (event: ReactPointerEvent<HTMLAnchorElement>) => {
    if (!active || prefersReducedMotion()) return;
    const rect = event.currentTarget.getBoundingClientRect();
    burst(rect.left + rect.width / 2, rect.top + rect.height / 2, 0.6);
    window.setTimeout(() => {
      burst(rect.left + rect.width * 0.15, rect.top - 10, 0.45);
      burst(rect.left + rect.width * 0.85, rect.top - 10, 0.45);
    }, 160);
  };

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={active ? `${className} dwl-btn` : className}
      onPointerDown={onPointerDown}
    >
      Grab the Deal Now
    </a>
  );
}

type Spark = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  c: string;
  life: number;
  max: number;
  sz: number;
  g: number;
  d: number;
};

type Rocket = {
  sx: number;
  sy: number;
  x: number;
  y: number;
  tx: number;
  ty: number;
  t: number;
  dur: number;
  sc: number;
};

type Flash = { x: number; y: number; a: number; r: number };

const PALETTE = [
  ["#ffd166", "#ffb703", "#fff3c4"],
  ["#ff4d8d", "#ff8fab", "#ffd6e5"],
  ["#22c55e", "#4ade80", "#dcfce7"],
  ["#ff7a18", "#ffb347", "#fff1d6"],
  ["#a78bfa", "#60a5fa", "#e0e7ff"],
];

function FireworksLayer({ burstRef }: { burstRef: MutableRefObject<Burst> }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || prefersReducedMotion()) return;
    const context = canvas.getContext("2d");
    if (!context) return;
    const ctx: CanvasRenderingContext2D = context;

    let vw = 0;
    let vh = 0;
    let running = false;
    let raf = 0;
    let alive = true;
    const parts: Spark[] = [];
    const rockets: Rocket[] = [];
    const flashes: Flash[] = [];

    const sizeCanvas = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      vw = window.innerWidth;
      vh = window.innerHeight;
      canvas.width = vw * dpr;
      canvas.height = vh * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const spark = (
      x: number,
      y: number,
      vx: number,
      vy: number,
      c: string,
      life: number,
      sz: number,
      grav: number,
      drag: number,
    ) => {
      parts.push({ x, y, vx, vy, c, life, max: life, sz, g: grav, d: drag });
    };

    const start = () => {
      if (!alive || running) return;
      running = true;
      raf = requestAnimationFrame(frame);
    };

    const burst = (x: number, y: number, sc = 1) => {
      const scale = sc || 1;
      const pal = PALETTE[(Math.random() * PALETTE.length) | 0];
      const n = Math.round(100 * Math.max(0.45, scale));
      const sp = (3 + Math.random() * 1.5) * scale;
      const ring = Math.random() < 0.35;
      for (let i = 0; i < n; i++) {
        const angle = (Math.PI * 2 * i) / n + Math.random() * 0.05;
        const speed = ring ? sp : sp * (0.45 + Math.random() * 0.55);
        spark(
          x,
          y,
          Math.cos(angle) * speed,
          Math.sin(angle) * speed,
          pal[i % 3],
          55 + Math.random() * 35,
          1.3 + Math.random() * 1.2 * scale,
          0.035,
          0.975,
        );
      }
      for (let j = 0; j < 26 * scale; j++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 1.5 * scale;
        spark(
          x,
          y,
          Math.cos(angle) * speed,
          Math.sin(angle) * speed,
          "#ffffff",
          38 + Math.random() * 28,
          1,
          0.02,
          0.97,
        );
      }
      flashes.push({ x, y, a: 0.8 * scale, r: 140 * scale });
      start();
    };

    const launch = (x: number, ty: number, sc = 1) => {
      rockets.push({
        sx: x,
        sy: vh + 10,
        x,
        y: vh + 10,
        tx: x + (Math.random() - 0.5) * 40,
        ty,
        t: 0,
        dur: 50 + Math.random() * 16,
        sc,
      });
      start();
    };

    function frame() {
      if (!alive) return;
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = "rgba(0,0,0,0.24)";
      ctx.fillRect(0, 0, vw, vh);
      ctx.globalCompositeOperation = "lighter";

      for (let r = rockets.length - 1; r >= 0; r--) {
        const rocket = rockets[r];
        rocket.t += 1;
        const p = Math.min(1, rocket.t / rocket.dur);
        const e = 1 - (1 - p) * (1 - p);
        rocket.x = rocket.sx + (rocket.tx - rocket.sx) * e;
        rocket.y = rocket.sy + (rocket.ty - rocket.sy) * e;
        for (let z = 0; z < 2; z++) {
          spark(
            rocket.x,
            rocket.y,
            (Math.random() - 0.5) * 0.7,
            Math.random() + 0.4,
            z ? "#ffb347" : "#fff3c4",
            18 + Math.random() * 12,
            1.2,
            0.03,
            0.96,
          );
        }
        ctx.fillStyle = "#fff7d6";
        ctx.beginPath();
        ctx.arc(rocket.x, rocket.y, 2.2, 0, Math.PI * 2);
        ctx.fill();
        if (p >= 1) {
          rockets.splice(r, 1);
          burst(rocket.x, rocket.y, rocket.sc);
        }
      }

      for (let i = parts.length - 1; i >= 0; i--) {
        const sparkle = parts[i];
        sparkle.vx *= sparkle.d;
        sparkle.vy *= sparkle.d;
        sparkle.vy += sparkle.g;
        sparkle.x += sparkle.vx;
        sparkle.y += sparkle.vy;
        sparkle.life -= 1;
        if (sparkle.life <= 0) {
          parts.splice(i, 1);
          continue;
        }
        let alpha = sparkle.life / sparkle.max;
        if (Math.random() < 0.12) alpha *= 0.35;
        ctx.globalAlpha = alpha;
        ctx.fillStyle = sparkle.c;
        ctx.beginPath();
        ctx.arc(sparkle.x, sparkle.y, sparkle.sz, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      for (let h = flashes.length - 1; h >= 0; h--) {
        const flash = flashes[h];
        const gradient = ctx.createRadialGradient(flash.x, flash.y, 0, flash.x, flash.y, flash.r);
        gradient.addColorStop(0, `rgba(255,220,150,${flash.a * 0.22})`);
        gradient.addColorStop(1, "rgba(255,220,150,0)");
        ctx.fillStyle = gradient;
        ctx.fillRect(flash.x - flash.r, flash.y - flash.r, flash.r * 2, flash.r * 2);
        flash.a *= 0.86;
        if (flash.a < 0.02) flashes.splice(h, 1);
      }

      if (parts.length || rockets.length || flashes.length) {
        raf = requestAnimationFrame(frame);
      } else {
        running = false;
        ctx.clearRect(0, 0, vw, vh);
      }
    }

    const blockers = () => {
      const nodes = document.querySelectorAll(
        "aside, h1, h2, h3, h4, p, li, button, a, .dwl-pill, .dwl-timer, .dwl-card, [data-diwali-block]",
      );
      const rects: DOMRect[] = [];
      nodes.forEach((node) => {
        const rect = node.getBoundingClientRect();
        if (rect.width >= 8 && rect.height >= 8 && rect.bottom > 0 && rect.top < vh) {
          rects.push(rect);
        }
      });
      return rects;
    };

    const contentX = () => {
      const sidebar = window.innerWidth >= 768 ? 256 : 0;
      const rtl = document.documentElement.dir === "rtl";
      if (rtl) return { min: 24, max: Math.max(80, window.innerWidth - sidebar - 24) };
      return { min: sidebar + 24, max: window.innerWidth - 24 };
    };

    const freeSpot = (sc: number) => {
      const blocked = blockers();
      const pad = 72 * sc;
      const range = contentX();
      const span = Math.max(40, range.max - range.min);
      for (let tries = 0; tries < 24; tries++) {
        const x = range.min + Math.random() * span;
        const y = vh * (0.08 + Math.random() * 0.62);
        const hit = blocked.some(
          (rect) => x > rect.left - pad && x < rect.right + pad && y > rect.top - pad && y < rect.bottom + pad,
        );
        if (!hit) return { x, y };
      }
      return null;
    };

    const ambient = () => {
      if (!alive || document.hidden) return;
      const sc = vw < 760 ? 0.5 : 0.7 + Math.random() * 0.25;
      const spot = freeSpot(sc);
      if (!spot) return;
      if (Math.random() < 0.5) launch(spot.x, spot.y, sc);
      else burst(spot.x, spot.y, sc * 0.85);
    };

    sizeCanvas();
    burstRef.current = burst;
    const onResize = () => sizeCanvas();
    window.addEventListener("resize", onResize);

    const volley = [0, 350, 700].map((delay) =>
      window.setTimeout(() => {
        if (!alive) return;
        const sc = vw < 760 ? 0.5 : 0.8;
        const spot = freeSpot(sc);
        if (spot) burst(spot.x, spot.y, sc);
      }, 700 + delay),
    );
    const ambientId = window.setInterval(ambient, FIREWORKS_EVERY);

    return () => {
      alive = false;
      burstRef.current = () => {};
      window.removeEventListener("resize", onResize);
      volley.forEach((id) => window.clearTimeout(id));
      window.clearInterval(ambientId);
      cancelAnimationFrame(raf);
    };
  }, [burstRef]);

  return (
    <div className="dwl-fx" aria-hidden="true">
      <canvas ref={canvasRef} />
    </div>
  );
}
